import { ChevronsUpDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { listContacts } from '../api'
import type { Ref } from './types'

const SEARCH_DELAY_MS = 250

/** The popover is portaled to <body>; keep its list scrollable inside a modal (as in OrganisationCombobox). */
const keepScrolling = (event: { stopPropagation: () => void }) => event.stopPropagation()

interface Answer {
  text: string
  items: Ref[] | null
}

/** Pick an existing contact for an agreement (or none). Only the answer to the text in the box is listed. */
export function ContactCombobox({
  value,
  onChange,
  label = 'Contact',
  excludeId,
}: {
  value: Ref | null
  onChange: (value: Ref | null) => void
  label?: string
  /** A contact that is not offered (the merge picker leaves out the page's own). */
  excludeId?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [answer, setAnswer] = useState<Answer | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const lookup = useRef(0)
  const trimmed = query.trim()
  const current = answer !== null && answer.text === trimmed ? answer : null
  const failed = current !== null && current.items === null
  const options = current?.items ?? []

  useEffect(() => () => window.clearTimeout(timer.current), [])

  function search(text: string, delay: number) {
    window.clearTimeout(timer.current)
    lookup.current += 1
    const mine = lookup.current
    function run() {
      listContacts({ q: text || undefined, limit: 8 })
        .then((result) => {
          if (mine === lookup.current) {
            setAnswer({ text, items: result.items
                .filter((item) => item.person_id !== excludeId)
                .map((item) => ({ id: item.person_id, name: item.full_name })),
            })
          }
        })
        .catch(() => {
          if (mine === lookup.current) setAnswer({ text, items: null })
        })
    }
    if (delay > 0) timer.current = window.setTimeout(run, delay)
    else run()
  }

  function choose(choice: Ref | null) {
    onChange(choice)
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) search(trimmed, 0)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-lg border border-border bg-background px-3 text-left text-sm max-md:h-11 max-md:text-base',
            !value && 'text-muted-foreground',
          )}
        >
          <span className="truncate">{value ? value.name : 'Geen contact'}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={(next) => {
              setQuery(next)
              search(next.trim(), SEARCH_DELAY_MS)
            }}
            placeholder="Zoek een contact…"
          />
          <CommandList onWheel={keepScrolling} onTouchMove={keepScrolling}>
            {current === null ? (
              <p role="status" className="py-4 text-center text-sm text-muted-foreground">
                Zoeken…
              </p>
            ) : failed ? (
              <p role="alert" className="py-4 text-center text-sm text-destructive">
                Zoeken mislukt. Probeer het opnieuw.
              </p>
            ) : (
              <CommandEmpty>Geen contacten gevonden.</CommandEmpty>
            )}
            <CommandGroup>
              {value && !trimmed ? (
                <CommandItem value="__geen__" onSelect={() => choose(null)}>
                  Geen contact
                </CommandItem>
              ) : null}
              {options.map((option) => (
                <CommandItem key={option.id} value={option.id} onSelect={() => choose(option)}>
                  <span className="break-words">{option.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
